'use client';

import { useEffect, useMemo, useState } from 'react';

function diff(from: Date, to: Date) {
  let years = to.getFullYear() - from.getFullYear();
  let months = to.getMonth() - from.getMonth();
  let days = to.getDate() - from.getDate();

  if (days < 0) {
    months -= 1;
    const prevMonth = new Date(to.getFullYear(), to.getMonth(), 0);
    days += prevMonth.getDate();
  }
  if (months < 0) {
    years -= 1;
    months += 12;
  }

  const totalDays = Math.floor((to.getTime() - from.getTime()) / 86_400_000);
  return { years, months, days, totalDays, totalWeeks: Math.floor(totalDays / 7) };
}

export function AgeCalculator() {
  const [birthDate, setBirthDate] = useState('2000-01-01');
  // "Today" must not be computed during render: the static build bakes in
  // whatever day it was built on, so recomputing new Date() at hydration
  // time (a visitor's real "now") would mismatch the server-rendered value
  // as soon as the calendar date rolls over. Resolve it client-side only,
  // after mount, same as the other tools with non-deterministic state.
  const [today, setToday] = useState<Date | null>(null);

  useEffect(() => {
    setToday(new Date());
  }, []);

  const result = useMemo(() => {
    if (!today) return null;
    const from = new Date(birthDate);
    if (Number.isNaN(from.getTime())) return null;
    if (from > today) return null;
    return diff(from, today);
  }, [birthDate, today]);

  return (
    <div className="rounded-2xl border border-border bg-card p-5">
      <label className="block">
        <span className="text-xs font-medium text-muted-foreground">Date of birth</span>
        <input
          type="date"
          name="birth-date"
          autoComplete="off"
          value={birthDate}
          max={today ? today.toISOString().slice(0, 10) : undefined}
          onChange={(e) => setBirthDate(e.target.value)}
          className="mt-1 h-10 w-full rounded-lg border border-input bg-background px-3 text-foreground outline-none focus:border-ring focus:ring-2 focus:ring-ring/30"
        />
      </label>

      {result ? (
        <div className="mt-6 grid grid-cols-3 gap-3">
          <Stat label="Years" value={result.years} />
          <Stat label="Months" value={result.months} />
          <Stat label="Days" value={result.days} />
        </div>
      ) : (
        <p className="mt-6 text-sm text-muted-foreground">
          Pick a date in the past to see your age.
        </p>
      )}

      {result && (
        <p className="mt-3 text-sm text-muted-foreground">
          That's {result.totalDays.toLocaleString()} days ({result.totalWeeks.toLocaleString()}{' '}
          weeks) old.
        </p>
      )}
    </div>
  );
}

function Stat({ label, value }: { label: string; value: number }) {
  return (
    <div className="rounded-xl bg-primary/10 px-3 py-4 text-center">
      <p className="text-2xl font-bold text-primary">{value}</p>
      <p className="text-xs font-medium text-muted-foreground">{label}</p>
    </div>
  );
}
